import React, { useState, useEffect, useMemo } from "react";
import { Box, Button, Dialog, DialogTitle, DialogContent, Typography, TextField, MenuItem, Checkbox, Chip, IconButton, Grid, CircularProgress } from "@mui/material";
import * as Yup from "yup";
import { useDispatch } from "store";
import { openSnackbar, closeSnackbar } from "store/slices/snackbar";
import { FiX, FiChevronDown } from "react-icons/fi";
import { Formik } from "formik";
import { useUpdateUserMutation, useGetUserDetailQuery } from "store/slices/apis/userApiSlice";
import { useGetAllProjectsQuery } from "store/slices/apis/projectApiSlice";
import { useGetUserAssetsQuery, useGetUserRoleListQuery } from "store/slices/apis/userRoleApiSlice";
import { useParams } from "react-router-dom";
import WbsTreeView from "view/dashboard/wbsTreeView";
import { useGetWbsLevelsQuery } from "store/slices/apis/scheduleSlice";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCreateBulkUserAssetsMutation, useUpdateBulkUserAssetsMutation } from "store/slices/apis/userAssetSlice";

const MapUser = (props) => {
    const dispatch = useDispatch();
    const { projectId } = useParams();
    const isProjectRoute = !!projectId;
    const { open, handleClose, rowDataId } = props;
    const { data: userDetail, isFetching: userLoader, isError: userError } = useGetUserDetailQuery(rowDataId);
    const userId = userDetail?.data?.id;
    //console.log("Props", props, userId)
    const { data: assetData, isFetching: assetLoader, isError: assetError } = useGetUserAssetsQuery(
        userId
            ? {
                projectId: projectId,
                userId: userId,
            }
            : skipToken)
    const userData = userDetail?.data;

    // const { data: wbsTreeData, isFetching: wbsLoader, isError: wbsError } =
    //     useGetWbsLevelsQuery(
    //         projectId ? { projectId, parentId: "919" } : skipToken
    //     );

    const [updateBulkUserAsset, { data, isLoading: updateAssetLoader, isError: updateAssetError }] = useUpdateBulkUserAssetsMutation();

    const initialValues = {
        email: userData?.email || "",
        firstName: userData?.firstName || "",
        lastName: userData?.lastName || "",
        selectedWbs: assetData || []
    };

    const schema = Yup.object().shape({
        email: Yup.string()
            .email("Enter a valid email address")
            .required("Email field is required"),
        firstName: Yup.string().required("First Name field is required"),
        lastName: Yup.string().required("Last Name field is required"),
        selectedWbs: Yup.array()
            .min(0, "Please select at least one WBS"),
    });

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        // let reqBody = {
        //     id: userData?.id,
        //     email: values?.email,
        //     firstName: values?.firstName,
        //     lastName: values?.lastName,
        // };
        // console.log("values", values)
        const userId = userData?.id;
        // const projectId = projectId;
        const requestBody = values?.selectedWbs?.length === 0 ? [{
            projectId,
            userId,
            assetName: "",
            assetActivityId: ""
        }] : values.selectedWbs.map(item => {
            return {
                projectId,
                userId,
                assetName: item.assetName,
                assetActivityId: item.assetActivityId || item.id
            }
        })

        // console.log("requestBody", requestBody)

        try {
            const response = await updateBulkUserAsset(requestBody).unwrap();
            console.log("Response", response)
            openNotification("Asset updated successfully!", 'success')
            resetForm();
            setSubmitting(false);
            handleClose();
        } catch (error) {
            openNotification(error?.data?.message || error?.error || "Something went wrong", "error");
            //handleClose();
        } finally {
            setSubmitting(false);
        }
    };

    const openNotification = (msg, type = "error", duration = 3000) => {
        dispatch(
            openSnackbar({
                open: true,
                message: msg,
                variant: 'alert',
                alert: {
                    color: type
                },
                close: false
            })
        );
        setTimeout(() => dispatch(closeSnackbar()), duration);
    };

    const labelTextStyle = {
        fontWeight: 500,
        fontSize: "16px",
        lineHeight: "100%",
        color: "#5F5F5F",
        fontFamily: "Outfit, sans-serif",
        mb: 1,
        mt: 3,
    };

    return (
        <>
            {/* ---------- Popup Dialog ---------- */}
            <Dialog
                open={open}
                fullWidth
                maxWidth="md"
                PaperProps={{
                    sx: { borderTopLeftRadius: "5px", borderTopRightRadius: "5px", backgroundColor: "#FFFFFF", width: "100%", overflowX: "hidden" },
                }}
                disableEscapeKeyDown
                onClose={(event, reason) => {
                    if (reason !== "backdropClick") {
                        handleClose();
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        borderBottom: "1px solid #ddd",
                        backgroundColor: "#ECECEC",
                        pb: 2,
                        pt: 3,
                        width: "100%",
                    }}
                >
                    <Box>
                        <Typography sx={{ fontWeight: 600, fontSize: "27px", lineHeight: "100%", color: "#140F36", fontFamily: "Outfit, sans-serif" }}>
                            Asset Mapping
                        </Typography>
                        <Typography sx={{
                            pt: 2, fontWeight: 400, fontSize: "14px", lineHeight: "100%", color: "#433F5E", fontFamily: "Outfit, sans-serif",
                        }}>
                            Map Assets to a user
                        </Typography>
                    </Box>

                </DialogTitle>
                {assetLoader || userLoader ? <Box sx={{ minHeight: 400, width: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><CircularProgress /></Box> : <DialogContent sx={{ width: "100%", p: 4, overflowX: "hidden", boxSizing: "border-box" }}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={schema}
                        enableReinitialize={true}
                        onSubmit={handleSubmit}
                    >
                        {({ values, handleSubmit, setFieldValue }) => (
                            <form onSubmit={handleSubmit}>
                                <Box sx={{ mt: 3 }}>

                                    {userError ? <>
                                        <Typography
                                            sx={{
                                                color: "error.main",
                                                mt: 1,
                                            }}
                                        >
                                            Failed to user details.
                                        </Typography>
                                    </> : <>
                                        <Typography sx={labelTextStyle}>Name</Typography>
                                        <Typography sx={{ mb: 2, fontSize: "16px" }}>
                                            {values?.firstName} {values?.lastName}
                                        </Typography>


                                        <Typography sx={labelTextStyle}>Email</Typography>
                                        <Typography sx={{ mb: 2, fontSize: "16px" }}>
                                            {values.email || "-"}
                                        </Typography>
                                    </>
                                    }


                                    <Grid item xs={12}>
                                        <Typography sx={{ mb: 1, fontWeight: 600 }}>
                                            Asset Mapping
                                        </Typography>


                                        <WbsTreeView
                                            projectId={projectId}
                                            parentId="683"
                                            onSelect={(node) => {
                                                // setFieldValue("wbsId", node.id);
                                                // setFieldValue("wbsName", node.name);
                                                const alreadySelected = values.selectedWbs.some(
                                                    item => item.assetActivityId === node.id
                                                );

                                                if (!alreadySelected) {
                                                    setFieldValue("selectedWbs", [
                                                        ...values.selectedWbs,
                                                        { assetActivityId: node.id, assetName: node.name }
                                                    ]);
                                                } else {
                                                    openNotification("Asset already added", "info");
                                                }
                                            }}
                                        />



                                        {/* Newly Selected WBS */}
                                        {values.selectedWbs?.length > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    border: "1px solid #CFE8F3",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#F4FBFF"
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: "#00344C",
                                                        mb: 1
                                                    }}
                                                >
                                                    Newly Selected
                                                </Typography>

                                                {values.selectedWbs?.map(currentItem => {
                                                    return <Chip
                                                        //key={currentItem?.assetName}
                                                        label={currentItem.assetName}
                                                        sx={{
                                                            backgroundColor: "#00344C",
                                                            color: "#fff",
                                                            fontWeight: 500,
                                                            m: 0.5,
                                                            "& .MuiChip-deleteIcon": {
                                                                color: "#fff",
                                                                "&:hover": {
                                                                    color: "#ccc"
                                                                }
                                                            }
                                                        }}
                                                        onDelete={() => {
                                                            const updated = values.selectedWbs.filter(
                                                                item => item.assetActivityId !== currentItem.assetActivityId
                                                            );
                                                            //console.log("updated", updated, values.selectedWbs)
                                                            setFieldValue("selectedWbs", updated);
                                                        }}
                                                    />
                                                })}
                                            </Box>
                                        )}

                                        {/* Already Mapped Assets */}
                                        {assetData?.length > 0 && (
                                            <Box
                                                sx={{
                                                    mt: 2,
                                                    p: 2,
                                                    border: "1px solid #E0E0E0",
                                                    borderRadius: "8px",
                                                    backgroundColor: "#FAFAFA"
                                                }}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: "#433F5E",
                                                        mb: 1
                                                    }}
                                                >
                                                    Already Mapped Assets
                                                </Typography>

                                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                                                    {assetData.map((item, index) => (
                                                        <Chip
                                                            key={index}
                                                            label={item?.assetName}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: "#E6E6E6",
                                                                fontSize: "12px"
                                                            }}
                                                        />
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                        {!assetLoader && assetError && (
                                            <Typography sx={{ color: "error.main", mt: 1 }}>
                                                Failed to load asset data.
                                            </Typography>
                                        )}
                                    </Grid>

                                    {/* Buttons */}
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: { xs: "column", md: "row" },
                                            gap: { xs: 2, md: 1 },
                                            mt: 4,
                                            justifyContent: "flex-end",
                                        }}
                                    >
                                        <Button
                                            onClick={handleClose}
                                            sx={{
                                                textTransform: "none",
                                                color: "#F15744",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                            }}
                                        >
                                            Cancel
                                        </Button>

                                        <Button
                                            type="submit"
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#00344C",
                                                borderRadius: "25px",
                                                textTransform: "none",
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                color: "#FFFFFF",
                                                px: "18px",
                                                py: "12px",
                                            }}
                                            loading={updateAssetLoader}
                                            disabled={updateAssetLoader}
                                        >
                                            {updateAssetLoader ? "Submitting" : "Submit"}
                                        </Button>
                                    </Box>
                                </Box>

                            </form>
                        )}
                    </Formik>

                </DialogContent>}
            </Dialog>
        </>
    );
};

export default MapUser;